/**
 * useLiveQuizTimer Hook
 * 
 * Manages the quiz timer state with server-synchronized timing.
 * Handles start, pause, resume, and bonus time functionality.
 * 
 * Timer uses serverTimeOffset to stay in sync with participants.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * @typedef {Object} TimerState
 * @property {number|null} timeRemaining - Seconds remaining (null if no timer)
 * @property {boolean} timerActive - Timer is running
 * @property {boolean} timerPaused - Timer is paused
 */

/**
 * @typedef {Object} TimerConfig
 * @property {boolean} enableTimer - Timer enabled
 * @property {number} timerDuration - Duration in seconds
 */

/**
 * @typedef {Object} TimerCallbacks
 * @property {Function} onTimerEnd - Called when timer reaches 0
 * @property {Function} onTimerSync - Called to sync timer state to Firebase
 */

/**
 * Hook to manage quiz timer
 * 
 * @param {Object} options
 * @param {TimerConfig} options.settings - Timer settings
 * @param {number} options.serverTimeOffset - Server time offset in ms
 * @param {number} options.questionIndex - Current question index
 * @param {TimerCallbacks} options.callbacks - Timer callbacks
 * @returns {TimerState & TimerActions}
 */
export function useLiveQuizTimer({
  settings = {},
  serverTimeOffset = 0,
  questionIndex = -1,
  callbacks = {}
}) {
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  
  // Internal state
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [bonusTimeAdded, setBonusTimeAdded] = useState(0);
  const [remainingWhenPaused, setRemainingWhenPaused] = useState(null);
  const [currentTimerDurationMs, setCurrentTimerDurationMs] = useState(null);
  
  // Refs for callbacks
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Timer settings
  const timerEnabled = settings?.enableTimer || settings?.enableQuestionTimer;
  const defaultDuration = settings?.timerDuration || settings?.questionTimerSeconds || 30;

  // ============================================
  // TIMER COUNTDOWN EFFECT
  // ============================================
  
  useEffect(() => {
    let interval = null;
    
    // If paused, show frozen value
    if (timerPaused && remainingWhenPaused !== null) {
      setTimeRemaining(Math.ceil(remainingWhenPaused / 1000));
      setTimerActive(false);
      return;
    }
    
    // No timer if not started or timer disabled
    if (!questionStartTime || questionIndex < 0 || !timerEnabled) {
      setTimeRemaining(null);
      setTimerActive(false);
      return;
    }
    
    // Use currentTimerDurationMs if set (after resume), otherwise use settings
    const timerDurationMs = currentTimerDurationMs || (defaultDuration * 1000);
    
    const calculateRemaining = () => {
      // Use server time for consistency with participants
      const serverTime = Date.now() + serverTimeOffset;
      const elapsed = serverTime - questionStartTime;
      const effectiveDuration = timerDurationMs + (bonusTimeAdded * 1000);
      return Math.max(0, Math.ceil((effectiveDuration - elapsed) / 1000));
    };
    
    // Initial calculation
    const initialRemaining = calculateRemaining();
    setTimeRemaining(initialRemaining);
    
    const isActive = initialRemaining > 0;
    setTimerActive(isActive);
    
    // Start countdown interval
    if (isActive) {
      interval = setInterval(() => {
        const remaining = calculateRemaining();
        setTimeRemaining(remaining);
        
        if (remaining <= 0) {
          setTimerActive(false);
          if (interval) {
            clearInterval(interval);
            interval = null;
          }
          // Call timer end callback
          callbacksRef.current.onTimerEnd?.();
        }
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [
    questionStartTime, 
    timerEnabled, 
    defaultDuration, 
    questionIndex, 
    bonusTimeAdded, 
    timerPaused, 
    remainingWhenPaused, 
    serverTimeOffset, 
    currentTimerDurationMs
  ]);

  // ============================================
  // TIMER ACTIONS
  // ============================================
  
  /**
   * Get current server time (local + offset)
   */
  const getServerTime = useCallback(() => {
    return Date.now() + serverTimeOffset;
  }, [serverTimeOffset]);

  /**
   * Start timer for a question
   */
  const startTimer = useCallback(() => {
    if (!timerEnabled) {
      setTimeRemaining(null);
      setTimerActive(false);
      callbacksRef.current.onTimerSync?.(null);
      return;
    }
    
    const serverTime = getServerTime();
    const durationMs = defaultDuration * 1000;
    
    // Reset state
    setBonusTimeAdded(0);
    setQuestionStartTime(serverTime);
    setTimerPaused(false);
    setRemainingWhenPaused(null);
    setCurrentTimerDurationMs(durationMs);
    setTimeRemaining(defaultDuration);
    setTimerActive(true);
    
    // Sync to Firebase
    callbacksRef.current.onTimerSync?.({
      questionStartedAt: serverTime,
      timerDurationMs: durationMs,
      bonusTimeAddedMs: 0,
      remainingMsWhenPaused: null,
      isPaused: false,
      questionIndex
    });
  }, [timerEnabled, defaultDuration, questionIndex, getServerTime]);

  /**
   * Stop timer completely
   */
  const stopTimer = useCallback(() => {
    setBonusTimeAdded(0);
    setQuestionStartTime(null);
    setTimeRemaining(null);
    setTimerActive(false);
    setTimerPaused(false);
    setRemainingWhenPaused(null);
    setCurrentTimerDurationMs(null);
    
    callbacksRef.current.onTimerSync?.(null);
  }, []);

  /**
   * Pause timer - saves remaining time
   */
  const pauseTimer = useCallback(() => {
    if (!timerActive || timerPaused) return;
    
    const remainingMs = timeRemaining ? timeRemaining * 1000 : 0;
    
    setTimerPaused(true);
    setTimerActive(false);
    setRemainingWhenPaused(remainingMs);
    
    callbacksRef.current.onTimerSync?.({
      questionStartedAt: questionStartTime,
      timerDurationMs: defaultDuration * 1000,
      bonusTimeAddedMs: bonusTimeAdded * 1000,
      remainingMsWhenPaused: remainingMs,
      isPaused: true,
      questionIndex
    });
  }, [timerActive, timerPaused, timeRemaining, questionStartTime, defaultDuration, bonusTimeAdded, questionIndex]);

  /**
   * Resume timer from paused state
   */
  const resumeTimer = useCallback(() => {
    if (!timerPaused || remainingWhenPaused === null) return;
    
    const serverTime = getServerTime();
    const resumeDuration = remainingWhenPaused;
    
    setTimerPaused(false);
    setTimerActive(true);
    setQuestionStartTime(serverTime);
    setBonusTimeAdded(0);
    setRemainingWhenPaused(null);
    setCurrentTimerDurationMs(resumeDuration);
    
    callbacksRef.current.onTimerSync?.({
      questionStartedAt: serverTime,
      timerDurationMs: resumeDuration,
      bonusTimeAddedMs: 0,
      remainingMsWhenPaused: null,
      isPaused: false,
      questionIndex
    });
  }, [timerPaused, remainingWhenPaused, questionIndex, getServerTime]);

  /**
   * Add bonus time (default 20 seconds)
   */
  const addBonusTime = useCallback((additionalSeconds = 20) => {
    if (timerPaused && remainingWhenPaused !== null) {
      // Timer is paused - add to remaining time
      const newRemainingMs = remainingWhenPaused + (additionalSeconds * 1000);
      setRemainingWhenPaused(newRemainingMs);
      setTimeRemaining(Math.ceil(newRemainingMs / 1000));
      
      callbacksRef.current.onTimerSync?.({
        questionStartedAt: questionStartTime,
        timerDurationMs: defaultDuration * 1000,
        bonusTimeAddedMs: (bonusTimeAdded + additionalSeconds) * 1000,
        remainingMsWhenPaused: newRemainingMs,
        isPaused: true,
        questionIndex
      });
      
      setBonusTimeAdded(bonusTimeAdded + additionalSeconds);
    } else {
      // Timer is running
      const newBonusTime = bonusTimeAdded + additionalSeconds;
      setBonusTimeAdded(newBonusTime);
      
      if (questionStartTime) {
        callbacksRef.current.onTimerSync?.({
          questionStartedAt: questionStartTime,
          timerDurationMs: defaultDuration * 1000,
          bonusTimeAddedMs: newBonusTime * 1000,
          remainingMsWhenPaused: null,
          isPaused: false,
          questionIndex
        });
      }
    }
  }, [timerPaused, remainingWhenPaused, bonusTimeAdded, questionStartTime, defaultDuration, questionIndex]);

  /**
   * Reset timer state (e.g., when navigating questions)
   */
  const resetTimer = useCallback(() => {
    setBonusTimeAdded(0);
    setQuestionStartTime(null);
    setRemainingWhenPaused(null);
    setCurrentTimerDurationMs(null);
    // Don't reset timeRemaining/timerActive - let effect handle it
  }, []);

  // ============================================
  // RETURN
  // ============================================
  
  return {
    // State
    timeRemaining,
    timerActive,
    timerPaused,
    timerEnabled,
    
    // Derived
    bonusTimeAdded,
    
    // Actions
    startTimer,
    stopTimer,
    pauseTimer,
    resumeTimer,
    addBonusTime,
    resetTimer,
    getServerTime
  };
}

export default useLiveQuizTimer;
